import { ReactNode, useState } from "react";
import { faAngleDown, faAngleUp } from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";

import { PriceSummaryItemKind, PriceSummary as PriceSummaryType } from "common/types/priceSummary";
import { getSummaryItems, SummaryItem } from "common/utils/summary";
import { Optional } from "common/utils/types";

import { Incentive, Rebate } from "../types/promotions";
import Icon from "./Icon";
import Spinner from "./Spinner";

import commonStyles from "../styles/components/PriceSummary.module.less";

type Styles = { rows?: string; row?: string; subrows?: string; label?: string; value?: string } & {
  [K in PriceSummaryItemKind]?: string;
};

export type PriceSummaryProps<T extends PriceSummaryType> = {
  data: T;
  styles: Styles;
  isLoading?: boolean;
  renderPromotionLink?: RenderPromotionLink;
  renderItemLabel?: RenderItemLabel;
};

type RenderPromotionLink = (props: PriceSummaryPromotionLinkProps) => ReactNode;

export type PriceSummaryPromotionLinkProps = { label: string } & (
  | {
      incentive: Incentive;
      rebate?: never;
    }
  | {
      incentive?: never;
      rebate: Rebate;
    }
);

export type RenderItemLabel = (props: {
  kind: PriceSummaryItemKind;
  label: string;
  className: Optional<string>;
}) => ReactNode;

type PriceSummaryRowProps = {
  item: SummaryItem;
  styles: Styles;
  renderPromotionLink: RenderPromotionLink;
  renderItemLabel?: RenderItemLabel;
  isLoading: boolean;
};

const PriceSummaryRow = ({
  item,
  styles,
  renderPromotionLink,
  renderItemLabel,
  isLoading,
}: PriceSummaryRowProps) => {
  const { kind, label, value, subrows } = item;
  const hasSubrows = !!subrows?.length;
  const [isExpanded, setIsExpanded] = useState(false);

  const getLabelContent = () => {
    if (renderItemLabel) {
      const customLabel = renderItemLabel({ kind, label, className: styles.label });
      if (customLabel) {
        return customLabel;
      }
    }

    if (hasSubrows) {
      return (
        <button
          type="button"
          className={cx(commonStyles.link, styles.label)}
          onClick={() => setIsExpanded((e) => !e)}
        >
          {label}{" "}
          <Icon className={commonStyles.angle} icon={isExpanded ? faAngleUp : faAngleDown} />
        </button>
      );
    }

    return <div className={cx(styles.label)}>{label}</div>;
  };

  return (
    <div>
      <div className={cx(styles.row, styles[kind])}>
        {getLabelContent()}
        <div className={cx(styles.value)}>{isLoading ? <Spinner /> : value}</div>
      </div>
      {hasSubrows && (
        <div
          className={cx(commonStyles.subrows, styles.subrows, !isExpanded && commonStyles.hidden)}
        >
          {subrows.map(({ label: subLabel, value: subValue, incentive, rebate }) => (
            <div key={subLabel} className={cx(styles.row)}>
              <div className={cx(styles.label)}>
                {incentive || rebate
                  ? renderPromotionLink({
                      ...(incentive ? { incentive } : { rebate: rebate as Rebate }),
                      label: subLabel,
                    })
                  : subLabel}
              </div>
              <div className={cx(styles.value)}>{subValue}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

type PriceSummaryComponentProps<T extends PriceSummaryType> = Omit<
  PriceSummaryProps<T>,
  "renderPromotionLink"
> & {
  renderPromotionLink: RenderPromotionLink;
};

/**
 * This component is structural and meant as a wrapper around getSummaryItems.
 * It's meant to ensure that we display labels & values consistently across the app,
 * but does not prescribe any styles so that consumers can have their own styles.
 * It shouldn't rely on any outside context like hooks.
 */
const PriceSummary = <T extends PriceSummaryType>({
  data,
  styles,
  renderPromotionLink,
  renderItemLabel,
  isLoading,
}: PriceSummaryComponentProps<T>) => {
  return (
    <div className={styles.rows || ""}>
      {getSummaryItems<T>(data).map((item) => (
        <PriceSummaryRow
          key={`${item.kind}-${item.value}`}
          item={item}
          styles={styles}
          renderPromotionLink={renderPromotionLink}
          renderItemLabel={renderItemLabel}
          isLoading={!!(item.kind === PriceSummaryItemKind.Total && isLoading)}
        />
      ))}
    </div>
  );
};

export default PriceSummary;
